import React from 'react';

import PropTypes from 'prop-types';

import { RenderIconType } from '@adsk/alloy-react-icon';
import { isWeaveTheme, useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import TypographyWidthHolder from './TypographyWidthHolder';

type TabTitleProps = StylableComponent<HTMLButtonElement> & {
  disabled?: boolean;
  active?: boolean;
  renderIcon?: RenderIconType;
  href?: string;
  'data-testid'?: string;
};

/**
 * Tab title - the clickable item used to switch between tabs.
 */
const TabTitle = React.forwardRef<HTMLButtonElement, TabTitleProps>(
  (
    {
      style,
      className,
      disabled,
      children,
      active,
      renderIcon,
      onClick,
      ...props
    },
    ref,
  ) => {
    const labelAndIcon = children && renderIcon;
    const theme = useTheme();
    const tabsTokens = theme.tokens?.component?.tabs;
    const isWeave = isWeaveTheme(theme);

    const typographyStyle = {
      fontFamily: tabsTokens?.fontFamily,
      fontWeight: tabsTokens?.active?.fontWeight,
      fontSize: tabsTokens?.fontSize,
      lineHeight: tabsTokens?.lineHeight,
    };

    const alloyFocusStyles = {
      '&:focus': {
        '&:before': {
          content: "' '",
          position: 'absolute' as const,
          bottom: tabsTokens?.focusShadow?.offset,
          right: 0,
          left: 0,
          borderBottom: `${tabsTokens?.focusShadow?.width} solid ${tabsTokens?.focusShadow?.color}`,
        },
      },
      '&:active:focus': {
        '&:before': {
          borderBottom: 0,
        },
      },
    };

    const weaveFocusStyles = {
      '&:focus': {
        outline: 'none',
        boxShadow: `0 4px 0 0 ${tabsTokens?.focusShadow?.color}`,
      },
    };

    return (
      <button
        {...props}
        ref={ref}
        className={className}
        disabled={disabled}
        type="button"
        role="tab"
        css={[
          {
            position: 'relative',
            display: 'flex',
            justifyContent: labelAndIcon ? 'space-between' : 'center',
            alignItems: 'center',
            margin: `0 ${tabsTokens?.marginHorizontal}`,
            textDecoration: 'none',
            minWidth: tabsTokens?.minWidth,

            '&:first-child': {
              marginLeft: 0,
            },

            '&:last-child': {
              marginRight: 0,
            },

            '&::-moz-focus-inner': {
              border: 0,
            },

            background: 'none',
            outline: 'none',
            border: 'none',
            ...(tabsTokens?.borderBottom && {
              borderBottom: tabsTokens?.borderBottom,
            }),
            cursor: 'pointer',
            padding: `${tabsTokens?.paddingTop ?? tabsTokens?.paddingVertical} ${tabsTokens?.paddingHorizontal} ${tabsTokens?.paddingBottom ?? tabsTokens?.paddingVertical}`,
            ...(tabsTokens?.marginBottom && {
              marginBottom: tabsTokens?.marginBottom,
            }),
            color: tabsTokens?.default?.color,
            fontFamily: tabsTokens?.fontFamily,
            fontWeight: tabsTokens?.fontWeight,
            fontSize: tabsTokens?.fontSize,
            lineHeight: tabsTokens?.lineHeight,

            '&:active': {
              ...(!disabled && {
                fontWeight: tabsTokens?.fontWeight,
              }),
            },

            ...(!disabled && {
              '&:hover': {
                color: tabsTokens?.hover?.color,
              },
              '&:hover:not(:focus)': {
                borderBottomColor: tabsTokens?.hover?.borderColor,
              },
            }),

            ...(!active && {
              fontWeight: tabsTokens?.fontWeight,
            }),

            ...(!active &&
              disabled && {
                color: tabsTokens?.disabled?.color,
                cursor: 'default',
                opacity: tabsTokens?.opacity?.disabled,
              }),

            ...(active && {
              color: tabsTokens?.active?.color,
              fontWeight: tabsTokens?.active?.fontWeight,
              borderBottomColor: tabsTokens?.active?.borderColor,
            }),

            ...(isWeave ? weaveFocusStyles : alloyFocusStyles),
          },
          style,
        ]}
        onClick={onClick}
      >
        {renderIcon?.({
          style: {
            ...(children && {
              marginRight: tabsTokens?.iconMargin,
            }),
            ...(disabled && {
              color: tabsTokens?.disabled?.iconColor,
            }),
          },
        })}
        <TypographyWidthHolder typographyStyle={typographyStyle}>
          {children}
        </TypographyWidthHolder>
      </button>
    );
  },
);

TabTitle.displayName = 'TabTitle';

TabTitle.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** whether this tab is disables */
  disabled: PropTypes.bool,
  /** whether this tab is currently active */
  active: PropTypes.bool,
  /** The content of the tab title */
  children: PropTypes.any,
  /** Render the icon */
  renderIcon: PropTypes.func,
  /** Handles title click */
  onClick: PropTypes.func,
};

export default TabTitle;
